import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { lastDayOfMonth, format, getDaysInMonth, lastDayOfYear, addDays, parseISO } from 'date-fns';
import { actions as planActions } from './store/slice';
import PlannerModals from './components/planner-modals';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
// DroppableArea removed - Planner Dashboard is view-only
import {
  SchedulerGrid,
} from './components';
import './planner-dashboard-view.css'; 
import { fetchPlanner, fetchByWorkTicketId, fetchProcessCompletionStatus, actions } from './store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectPlannerData, isPlannerLoading, selectChannels, selectPrograms, selectFilteredPlans, selectPlanFilters } from './store/selectors';
import { fetchLocations } from '../../core/redux/location/slice';
import { usePlanner } from './usePlanner';
import { PlannerService } from '../../service/api/planner-service';

const PlannerDashboardView = () => {
  const plannerRef = useRef(null);
  const {
    isLoading,
    getEpgProps,
    getLayoutProps,
    onChangeDate,
    calendarMode,
    dates,
    removeElement,
  } = usePlanner(plannerRef);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    (new Date().getMonth() + 1).toString().padStart(2, '0'),
  ); // Start with current month selected
  const [selectedDay, setSelectedDay] = useState('');
  const [viewMode, setViewMode] = useState('month'); // Start in month view
  const [lastAction, setLastAction] = useState('selector'); // 'selector' or 'search' - tracks which was used last
  const [centerYear, setCenterYear] = useState(new Date().getFullYear()); // Track center year for multi-year view
  // Initialize with current month range
  const currentYear = new Date().getFullYear();
  const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
  const [start, setStart] = useState(`${currentYear}-${currentMonth}-01`);
  const [end, setEnd] = useState(
    `${currentYear}-${(new Date().getMonth() + 2).toString().padStart(2, '0')}-01`,
  );
  const [status, setStatus] = useState('All');
  const [hideEmptyMachines, setHideEmptyMachines] = useState(true); // Default to true for dashboard - show only allocated machines
  
  // ========================================
  // AUTO-REFRESH CONFIGURATION
  // ========================================
  // Set to 'true' to enable automatic refresh every minute
  // Set to 'false' to disable auto-refresh
  const AUTO_REFRESH_ENABLED = true;
  // ========================================
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  
  const dispatch = useDispatch();
  const channels = useSelector(selectChannels);
  const allPrograms = useSelector(selectPrograms);
  const filteredPrograms = useSelector(selectFilteredPlans);
  const planFilters = useSelector(selectPlanFilters);

  // filteredChannels moved below smartTimelineHeader to avoid TDZ

  const handleHideEmptyMachinesChange = (value) => {
    setHideEmptyMachines(value);
  };

  useEffect(() => {
    dispatch(fetchPlanner());
    dispatch(fetchProcessCompletionStatus());
    dispatch(
      fetchLocations({
        name: '',
        description: '',
      }),
    );
  }, []);

  // Auto-refresh functionality - refreshes every minute when enabled
  useEffect(() => {
    if (AUTO_REFRESH_ENABLED) {
      // Set up interval to refresh every minute (60000ms)
      const interval = setInterval(async () => {
        console.log('Auto-refreshing planner data...', new Date().toLocaleTimeString());
        setIsAutoRefreshing(true);
        
        try {
          await dispatch(fetchPlanner()).unwrap();
          await dispatch(fetchProcessCompletionStatus()).unwrap();
          
          setLastRefreshTime(new Date());
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        } finally {
          // Hide loading indicator after 1 second
          setTimeout(() => {
            setIsAutoRefreshing(false);
          }, 1000);
        }
      }, 60000); // 60 seconds

      // Cleanup interval on component unmount
      return () => {
        clearInterval(interval);
      };
    }
  }, [AUTO_REFRESH_ENABLED, dispatch, planFilters]);


  const generateYearsList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear + 2;
    const endYear = currentYear - 5;
    return Array.from({ length: startYear - endYear + 1 }, (_, index) => startYear - index);
  }, []);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const date = new Date(2000, index, 1); // Using 2000 as dummy year
      return {
        value: month.toString().padStart(2, '0'),
        label: format(date, 'MMMM'),
      };
    });
  }, []);

  const days = useMemo(() => {
    if (!selectedYear || !selectedMonth) return [];
    const daysInMonth = getDaysInMonth(new Date(selectedYear, parseInt(selectedMonth) - 1));
    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      return {
        value: day.toString().padStart(2, '0'),
        label: day.toString().padStart(2, '0'),
      };
    });
  }, [selectedYear, selectedMonth]);

  // Smart timeline header based on date range
  const timelineHeader = useMemo(() => {
    if (!selectedYear) {
      // Years view - show multiple years when no year is selected
      const baseYear = centerYear; // Use centerYear for navigation
      const startYear = baseYear - 2; // Show 2 years before
      const endYear = baseYear + 3;   // Show 3 years after
      const yearsToShow = endYear - startYear + 1;
      
      return Array.from({ length: yearsToShow }, (_, index) => {
        const year = startYear + index;
        return {
          label: year.toString(),
          year: year,
          iso: `${year}-01-01`,
          endIso: `${year}-12-31`,
          viewMode: 'year-multi'
        };
      });
    } else if (selectedDay) {
      // Day view - show hourly timeline (00:00 - 23:00)
      const selectedDate = new Date(selectedYear, parseInt(selectedMonth) - 1, parseInt(selectedDay));
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      return Array.from({ length: 24 }, (_, hour) => {
        return {
          label: `${hour.toString().padStart(2, '0')}:00`,
          hour: hour,
          iso: `${dateStr}T${hour.toString().padStart(2, '0')}:00:00`,
          date: dateStr,
          viewMode: 'hour'
        };
      });
    } else if (selectedMonth) {
      // Month view - show individual days within the selected month
      const daysInMonth = getDaysInMonth(new Date(selectedYear, parseInt(selectedMonth) - 1));
      return Array.from({ length: daysInMonth }, (_, index) => {
        const day = index + 1;
        const date = new Date(selectedYear, parseInt(selectedMonth) - 1, day);
        return {
          label: format(date, 'EEE dd'),
          iso: format(date, 'yyyy-MM-dd'),
          viewMode: 'day'
        };
      });
    } else {
      // Year view (no month selected) - show months (Jan, Feb, Mar, etc.)
      return Array.from({ length: 12 }, (_, index) => {
        const date = new Date(selectedYear, index, 1);
        const lastDay = lastDayOfMonth(date);
        return {
          label: format(date, 'MMM'),
          year: selectedYear,
          iso: format(date, 'yyyy-MM-dd'),
          endIso: format(lastDay, 'yyyy-MM-dd'),
          viewMode: 'month',
          monthIndex: index
        };
      });
    }
  }, [selectedYear, selectedMonth, selectedDay, centerYear]);

  // Timeline for filter dates - respect the last action (search or selector)
  const smartTimelineHeader = useMemo(() => {
    // If last action was using year/month/day selectors, use selector-based timeline
    if (lastAction === 'selector') {
      return timelineHeader;
    }
    
    // If last action was search, show day-based timeline with the searched date range
    if (lastAction === 'search' && start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      const daysDiff = Math.max(1, Math.min(31, diff)); // cap to 31 columns
      // Adjust end if needed to respect 31-day max
      const adjustedEnd = new Date(startDate);
      adjustedEnd.setDate(startDate.getDate() + (daysDiff - 1));
      
      // Show individual days with weekday format (Mon 05, Tue 06, etc.)
      return Array.from({ length: daysDiff }, (_, index) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + index);
        return {
          label: format(date, 'EEE dd'),
          dow: format(date, 'EEE'),
          day: format(date, 'dd'),
          iso: format(date, 'yyyy-MM-dd'),
          viewMode: 'day'
        };
      });
    }
    
    // Fallback to timelineHeader
    return timelineHeader;
  }, [start, end, timelineHeader, lastAction]);

  // Helper function to parse date strings in local timezone (avoid UTC conversion issues)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    // If it's already a date-only string (yyyy-MM-dd), parse it as local midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }
    // Otherwise, use parseISO and convert to local
    try {
      const date = parseISO(dateStr);
      // Return as local date (midnight local time)
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    } catch {
      return null;
    }
  };

  // Compute the set of programs to render based on the visible timeline
  const effectivePrograms = useMemo(() => {
    if (smartTimelineHeader && smartTimelineHeader.length > 0) {
      const first = smartTimelineHeader[0];
      const last = smartTimelineHeader[smartTimelineHeader.length - 1];
      const startIso = first.iso || first.date;
      // For month view, use endIso if available (last day of last month), otherwise use iso
      const endIso = last.endIso || last.iso || last.date;
      
      if (startIso && endIso) {
        const vs = parseLocalDate(startIso);
        // For month view, endIso is the last day of the last month (e.g., "2025-12-31")
        // Parse it and ensure it includes the full day
        let ve = parseLocalDate(endIso);
        if (!ve) {
          // Fallback: compute end date from start
          ve = new Date(vs);
          ve.setMonth(ve.getMonth() + 12); // Add 12 months for year view
        }
        // Set to end of day to include the entire last day
        ve.setHours(23, 59, 59, 999);
        
        // Debug: Log timeline header info for year view
        if (smartTimelineHeader[0]?.viewMode === 'month' && smartTimelineHeader.length === 12) {
          console.log('[Year View Debug] Timeline Header:', {
            headerLength: smartTimelineHeader.length,
            firstMonth: smartTimelineHeader[0],
            lastMonth: smartTimelineHeader[11],
            startIso: startIso,
            endIso: endIso,
            timelineStart: vs.toISOString(),
            timelineEnd: ve.toISOString(),
            selectedYear: selectedYear,
            selectedMonth: selectedMonth,
            viewMode: viewMode,
            lastAction: lastAction
          });
        }
        
        return allPrograms.filter(p => {
          if (!p?.since || !p?.till) return false;
          const ps = parseLocalDate(p.since.split('T')[0]); // Use date part only
          const pe = parseLocalDate(p.till.split('T')[0]); // Use date part only
          
          if (!ps || !pe) return false;
          
          // Set program end to end of day
          const peEndOfDay = new Date(pe);
          peEndOfDay.setHours(23, 59, 59, 999);
          
          // Check if program overlaps with visible timeline
          // Program overlaps if: program starts before timeline ends AND program ends after timeline starts
          const overlaps = ps <= ve && peEndOfDay >= vs;
          
          // Debug logging for specific program (id: 1444)
          if (p.id === '1444' || p.id === 1444) {
            console.log('[Month View Debug] Program 1444:', {
              programSince: p.since,
              programTill: p.till,
              parsedSince: ps.toISOString(),
              parsedTill: peEndOfDay.toISOString(),
              timelineStart: vs.toISOString(),
              timelineEnd: ve.toISOString(),
              overlaps: overlaps,
              startIso: startIso,
              endIso: endIso,
              smartTimelineHeaderLength: smartTimelineHeader.length,
              smartTimelineHeaderFirst: smartTimelineHeader[0],
              smartTimelineHeaderLast: smartTimelineHeader[smartTimelineHeader.length - 1]
            });
          }
          
          return overlaps;
        });
      }
    }
    return filteredPrograms;
  }, [smartTimelineHeader, allPrograms, filteredPrograms, selectedYear, selectedMonth, viewMode, lastAction]);

  // Filter channels based on hideEmptyMachines setting
  // Uses the actual visible timeline (smartTimelineHeader) to decide allocations
  const filteredChannels = useMemo(() => {
    if (!hideEmptyMachines) return channels;

    const keyOfChannel = c =>
      c?.uuid || c?.id || c?.assetNumber || c?.title || String(c?.index || '');

    // Determine visible range from smartTimelineHeader (what user actually sees)
    let visibleStartDate = null;
    let visibleEndDate = null;
    if (smartTimelineHeader && smartTimelineHeader.length > 0) {
      const first = smartTimelineHeader[0];
      const last = smartTimelineHeader[smartTimelineHeader.length - 1];
      const firstIso = first.iso || first.date;
      const lastIso = last.endIso || last.iso || last.date;
      if (firstIso && lastIso) {
        visibleStartDate = new Date(firstIso);
        visibleEndDate = new Date(lastIso);
        visibleEndDate.setHours(23, 59, 59, 999);
      }
    }

    if (!visibleStartDate || !visibleEndDate) {
      if (start && end) {
        visibleStartDate = new Date(start);
        visibleEndDate = new Date(end);
      }
    }

    // Build set of program keys that overlap the visible range
    const programKeys = new Set();
    effectivePrograms.forEach(program => {
      if (visibleStartDate && visibleEndDate && program.since && program.till) {
        const ps = new Date(program.since);
        const pe = new Date(program.till);
        const overlaps = ps <= visibleEndDate && pe >= visibleStartDate;
        if (!overlaps) return;
      }
      [
        program.channelUuid,
        program.channelId,
        program.assetNumber,
        program.channel,
        program.machineId,
      ].filter(Boolean).map(String).forEach(id => {
        if (id && id !== 'undefined' && id !== '') programKeys.add(id);
      });
    });

    return channels.filter((channel, index) => {
      const channelIdentifiers = [
        channel.uuid,
        channel.id,
        channel.assetNumber,
        channel.title,
        keyOfChannel(channel),
        keyOfChannel({ ...channel, index }),
      ].filter(Boolean).map(String);
      return channelIdentifiers.some(id => programKeys.has(id));
    });
  }, [channels, effectivePrograms, hideEmptyMachines, smartTimelineHeader, start, end]);

  const handleYearUnselect = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setViewMode('years'); // Set to multi-year view
    setLastAction('selector');
    const currentYear = new Date().getFullYear();
    setCenterYear(currentYear); // Track the center year for navigation
    const startYear = currentYear - 2;
    const endYear = currentYear + 3;
    const yearsStart = `${startYear}-01-01`;
    const yearsEnd = `${endYear}-12-31`;
    setStart(`${yearsStart}T00:00:00`);
    setEnd(`${yearsEnd}T24:00:00`);
  };

  const handleYearChange = event => {
    const year = event.target.value;
    if (year === '') {
      handleYearUnselect();
      return;
    }
    setSelectedYear(year);
    setSelectedMonth(''); // Reset month
    setSelectedDay(''); // Reset day
    setViewMode('year'); // Set to year view
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${year}-01-01`;
    // Explicitly set the date range to cover the entire year
    const yearStart = `${year}-01-01`;
    const yearEnd = format(lastDayOfYear(new Date(year, 0, 1)), 'yyyy-MM-dd');
    const yearStartISO = `${yearStart}T00:00:00`;
    const yearEndISO = `${yearEnd}T24:00:00`;
    setStart(yearStartISO);
    setEnd(yearEndISO);
    
    // Update Redux filter state to ensure all programs in the year are shown
    dispatch(actions.setFilters({
      start: yearStart,
      end: yearEnd,
      status: status
    }));
    
    handleTimelineClick(startDate, 'year');
  };

  const handleMonthUnselect = () => {
    setSelectedMonth('');
    setSelectedDay('');
    setViewMode('year'); // Set to year view when month is unselected
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-01-01`;
    // Explicitly set the date range to cover the entire year
    const yearStart = `${selectedYear}-01-01`;
    const yearEnd = format(lastDayOfYear(new Date(selectedYear, 0, 1)), 'yyyy-MM-dd');
    const yearStartISO = `${yearStart}T00:00:00`;
    const yearEndISO = `${yearEnd}T24:00:00`;
    setStart(yearStartISO);
    setEnd(yearEndISO);
    
    // Update Redux filter state to ensure all programs in the year are shown
    dispatch(actions.setFilters({
      start: yearStart,
      end: yearEnd,
      status: status
    }));
    
    handleTimelineClick(startDate, 'year');
  };

  const handleMonthChange = event => {
    const month = event.target.value;
    if (month === '') {
      handleMonthUnselect();
      return;
    }
    setSelectedMonth(month);
    setSelectedDay('');
    setViewMode('month'); // Set to month view (shows days)
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-${month}-01`;
    handleTimelineClick(startDate, 'month');
  };

  const handleDayUnselect = () => {
    setSelectedDay('');
    setViewMode('month'); // Back to month view
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-${selectedMonth}-01`;
    handleTimelineClick(startDate, 'month');
  };

  const handleDayChange = event => {
    const day = event.target.value;
    if (day === '') {
      handleDayUnselect();
      return;
    }
    setSelectedDay(day);
    setViewMode('day'); // Set to day view (shows week)
    setLastAction('selector'); // Mark that selector was used
    const startDate = `${selectedYear}-${selectedMonth}-${day}`;
    handleTimelineClick(startDate, 'week');
  };

  const handleTimelineClick = (date, type) => {
    let startDate, endDate;
    if (type === 'month') {
      startDate = `${date}T00:00:00`;
      endDate = format(
        new Date(new Date(startDate).setMonth(new Date(startDate).getMonth() + 1)),
        "yyyy-MM-dd'T'24:00:00",
      );
      setSelectedMonth(format(new Date(date), 'MM'));
      setSelectedDay('');
      onChangeDate(startDate, endDate, 'week');
      handleStartEndDateChange(startDate, endDate);
    } else if (type === 'week') {
      startDate = `${date}T00:00:00`;
      endDate = `${date}T24:00:00`;
      const day = format(new Date(date), 'dd');
      setSelectedDay(day);
      onChangeDate(startDate, endDate, 'day');
      handleStartEndDateChange(startDate, endDate);
    } else if (type === 'year') {
      startDate = `${date}T00:00:00`;
      endDate = format(lastDayOfYear(new Date(startDate)), "yyyy-MM-dd'T'24:00:00");
      onChangeDate(startDate, endDate, 'month');
      handleStartEndDateChange(startDate, endDate);
    }
  };

  const handleStartEndDateChange = (start, end) => {
    setStart(start);
    setEnd(end);
  };

  const handleSearch = (fromDate, toDate, currentStatus) => {
    let startDate, endDate;
    setStatus(currentStatus);
    setLastAction('search'); // Mark that search was used - this takes priority
    
    startDate = `${fromDate}T00:00:00`;
    endDate = format(addDays(new Date(toDate), 1), "yyyy-MM-dd'T'00:00:00");
    
    // Update Redux filter state
    dispatch(actions.setFilters({
      start: fromDate,
      end: toDate,
      status: currentStatus
    }));
    
    onChangeDate(startDate, endDate, 'week');
    handleStartEndDateChange(startDate, endDate);
  };

  const handleProgramEdit = id => {
    dispatch(fetchByWorkTicketId(id));
  };

  // Helper function to safely extract date string from ISO date
  const getDateString = (dateValue) => {
    if (!dateValue) return '';
    try {
      // If it's already in YYYY-MM-DD format, return it
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      // If it has time component, extract date part
      if (typeof dateValue === 'string' && dateValue.includes('T')) {
        return dateValue.split('T')[0];
      }
      // Try to parse as Date
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().split('T')[0];
    } catch (e) {
      return '';
    }
  };

  // Navigation logic for timeline arrows - respects current view mode
  const handleTimelineNavigation = (direction) => {
    let newYear, newMonth, newDay;
    const multiplier = direction === 'prev' ? -1 : 1;
    
    if (viewMode === 'years') {
      // Multi-year view - navigate by 6 years (the span shown)
      newYear = centerYear + (multiplier * 6);
      setCenterYear(newYear); // Update center year for navigation
      setSelectedYear(''); // Keep year unselected
      setSelectedMonth('');
      setSelectedDay('');
      const yearsStart = `${newYear - 2}-01-01`;
      const yearsEnd = `${newYear + 3}-12-31`;
      setStart(`${yearsStart}T00:00:00`);
      setEnd(`${yearsEnd}T24:00:00`);
      setLastAction('selector');
    } else if (viewMode === 'year') {
      // Yearly view - navigate by 1 year
      newYear = parseInt(selectedYear) + multiplier;
      setSelectedYear(newYear);
      const yearStart = `${newYear}-01-01`;
      const yearEnd = format(lastDayOfYear(new Date(newYear, 0, 1)), 'yyyy-MM-dd');
      setStart(`${yearStart}T00:00:00`);
      setEnd(`${yearEnd}T24:00:00`);
      handleTimelineClick(yearStart, 'year');
    } else if (viewMode === 'month') {
      // Monthly view - navigate by 1 month
      let monthNum = parseInt(selectedMonth) + multiplier;
      let yearNum = parseInt(selectedYear);
      
      if (monthNum > 12) {
        monthNum = 1;
        yearNum++;
      } else if (monthNum < 1) {
        monthNum = 12;
        yearNum--;
      }
      
      setSelectedYear(yearNum);
      setSelectedMonth(monthNum.toString().padStart(2, '0'));
      const monthStart = `${yearNum}-${monthNum.toString().padStart(2, '0')}-01`;
      handleTimelineClick(monthStart, 'month');
    } else if (viewMode === 'day' || viewMode === 'hour') {
      // Day/Hour view - navigate by 1 day
      const currentDate = new Date(selectedYear, parseInt(selectedMonth) - 1, parseInt(selectedDay));
      const newDate = addDays(currentDate, multiplier);
      
      setSelectedYear(newDate.getFullYear());
      setSelectedMonth((newDate.getMonth() + 1).toString().padStart(2, '0'));
      setSelectedDay(newDate.getDate().toString().padStart(2, '0'));
      const dayStart = format(newDate, 'yyyy-MM-dd');
      handleTimelineClick(dayStart, 'week');
    } else {
      // Fallback to old behavior for search mode
      const currentStart = start || new Date().toISOString();
      const currentEnd = end || new Date(Date.now() + 24 * 3600 * 1000).toISOString();
      const daysDiff = Math.ceil((new Date(currentEnd) - new Date(currentStart)) / (1000 * 60 * 60 * 24));
      
      const newStart = format(addDays(new Date(currentStart), multiplier * daysDiff), 'yyyy-MM-dd');
      const newEnd = format(addDays(new Date(currentEnd), multiplier * daysDiff), 'yyyy-MM-dd');
      
      handleSearch(newStart, newEnd, status);
    }
  };

  // Handle header cell click to drill into year/month/day views (no Planby)
  const handleHeaderSelection = (header) => {
    if (!header) return;
    setLastAction('selector');

    // If the header contains a specific viewMode, use it to decide behavior
    const headerView = header.viewMode || viewMode;

    try {
      if (headerView === 'year-multi' && header.year) {
        // Click on a year cell in multi-year view -> go to that year
        handleYearChange({ target: { value: header.year } });
        return;
      }

      if (headerView === 'month') {
        // Click on a month cell -> go to that month (day view of that month)
        const iso = header.iso || header.label;
        if (iso) {
          const dateObj = parseISO(iso);
          const yr = format(dateObj, 'yyyy');
          const mo = format(dateObj, 'MM');
          setSelectedYear(yr);
          setSelectedMonth(mo);
          setSelectedDay('');
          setViewMode('month');
          handleTimelineClick(`${yr}-${mo}-01`, 'month');
        }
        return;
      }

      // Default / day view click -> go to day/hour view
      const iso = header.iso || header.date;
      if (iso) {
        const dateObj = parseISO(iso);
        const yr = format(dateObj, 'yyyy');
        const mo = format(dateObj, 'MM');
        const dy = format(dateObj, 'dd');
        setSelectedYear(yr);
        setSelectedMonth(mo);
        setSelectedDay(dy);
        setViewMode('day');
        handleTimelineClick(`${yr}-${mo}-${dy}`, 'week');
      }
    } catch (e) {
      console.warn('Failed to handle header selection', e);
    }
  };


  return (
    <>
      {/* Fullscreen Dashboard View - No sidebar, full width */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        overflow: 'hidden',
      }}>
        <div style={{ flexShrink: 0 }}>
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Production planning</h4>
              </div>
            </div>
          </div>
        </div>
        
        {/* Back Button - Top Right Corner */}
        <button
          type="button"
          className="btn btn-warning btn-sm"
          onClick={() => window.history.back()}
          title="Go Back"
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            padding: '10px 20px',
          }}
        >
          <i className="fas fa-arrow-left me-1"></i>
          Back
        </button>

        {/* View-only mode - DndProvider kept for context but drag-and-drop is disabled */}
        <DndProvider backend={HTML5Backend}>
          <div ref={plannerRef} className={selectedDay ? 'day-selected' : ''} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Auto-refresh indicator */}
            <div className="d-flex justify-content-between align-items-center mb-3 planner-meta-row flex-wrap gap-3" style={{ flexShrink: 0 }}>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {AUTO_REFRESH_ENABLED && (
                  <div className="d-flex align-items-center gap-2">
                    {isAutoRefreshing ? (
                      <div className="d-flex align-items-center gap-2 text-primary">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Refreshing...</span>
                        </div>
                        <span className="fw-bold">Refreshing planner data...</span>
                      </div>
                    ) : (
                      lastRefreshTime && (
                        <small className="text-muted">
                          <i className="fas fa-check-circle text-success"></i> Last refreshed: {lastRefreshTime.toLocaleTimeString()}
                        </small>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                width: '100%',
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              {/* DroppableArea removed - view-only mode */}
              <div style={{ marginBottom: 16 }}>
                <SchedulerGrid
                  channels={filteredChannels}
                  programs={effectivePrograms}
                  dates={dates}
                  timelineHeader={smartTimelineHeader}
                  filterDates={lastAction === 'search' ? { start, end } : null}
                  timelineWidth={plannerRef.current ? plannerRef.current.offsetWidth - 16 : 1000}
                  onProgramEdit={handleProgramEdit}
                  sidebarWidth={45}
                  showHeaderLabel={false}
                  onNavigateTimeline={handleTimelineNavigation}
                  onHeaderClick={handleHeaderSelection}
                  viewMode={viewMode}
                />
              </div>
            </div>
          </div>
        </DndProvider>
      </div>
      <PlannerModals />
    </>
  );
};

export default PlannerDashboardView;
